/**
 * Zwergenwald - Einführungsangebot (O-93 Runde 5, Inhaber 30.09.2026, Kanzlei-Punkte 6 und 7)
 *
 * Zwei Custom Elements (wie kleine Client-Komponenten):
 *   <zw-angebot-uhr data-ende="2026-10-07T23:59">   blocks/zw-angebot.liquid - der Countdown
 *   <zw-angebot-steuerung data-ende="...">           blocks/zw-angebot-steuerung.liquid - global, im Fußbereich
 *
 * data-ende ist ein Datum in BERLINER Zeit (Theme-Einstellung "Ende des Einführungsangebots"). Der Browser
 * rechnet es unabhängig von der Zeitzone des Besuchers in einen festen Zeitpunkt um (Intl mit
 * "Europe/Berlin", Sommer- und Winterzeit inklusive). Es gilt die Uhr des Geräts.
 *
 * Mit Ablauf setzt die Steuerung <html data-zw-angebot="abgelaufen">: Dann blenden CSS-Regeln in
 * assets/zwergenwald-marke.css den durchgestrichenen Preis im ganzen Shop aus, und Countdown und
 * Angebotszeile verschwinden. Der Wert "streich-aus" (Schalter in den Theme-Einstellungen) blendet nur den
 * Streichpreis aus. Es ändert sich KEIN Preis - das Theme fasst keinen Preis an.
 * Shopify legt gerenderte Seiten zwischen; diese Prüfung im Browser deckt den Ablauf trotzdem
 * sekundengenau ab.
 */
const BERLIN = 'Europe/Berlin';

/** Unterschied Berliner Ortszeit minus UTC zum Zeitpunkt utcMs, in Millisekunden. */
function berlinVersatz(utcMs) {
  const teile = new Intl.DateTimeFormat('en-US', {
    timeZone: BERLIN,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(new Date(utcMs));
  const w = Object.fromEntries(teile.filter((t) => t.type !== 'literal').map((t) => [t.type, Number(t.value)]));
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - Math.floor(utcMs / 1000) * 1000;
}

/**
 * "2026-10-07T23:59" (Berliner Ortszeit) -> Zeitpunkt in Millisekunden seit 1970 (UTC).
 * @param {string | undefined} iso
 * @returns {number | null}
 */
export function berlinZuEpoche(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(iso || '');
  if (!m) return null;
  const [j, mo, t, h, mi] = m.slice(1).map(Number);
  const ortszeit = Date.UTC(j, mo - 1, t, h, mi, 0);
  // 31.02. o. ae. wuerde ueberrollen (Date.UTC rechnet auf den Folgemonat): dann kein gueltiges Datum
  const probe = new Date(ortszeit);
  if (probe.getUTCFullYear() !== j || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== t) return null;
  let utc = ortszeit - berlinVersatz(ortszeit);
  utc = ortszeit - berlinVersatz(utc);
  return utc;
}

function ablaufMelden() {
  document.documentElement.setAttribute('data-zw-angebot', 'abgelaufen');
  document.dispatchEvent(new CustomEvent('zw:angebot-abgelaufen'));
}

class ZwAngebotUhr extends HTMLElement {
  /** @type {number | null} */
  #ende = null;
  /** @type {ReturnType<typeof setInterval> | null} */
  #takt = null;

  static get observedAttributes() {
    return ['hidden'];
  }

  /**
   * Beim Variantenwechsel gleicht assets/zw-variantenblock.js das Element mit dem Server-HTML ab
   * (dort steht "hidden", weil ohne JavaScript kein Countdown laufen kann). Läuft der Takt noch,
   * wird das Attribut sofort wieder entfernt.
   */
  attributeChangedCallback() {
    if (this.#takt && this.hasAttribute('hidden')) {
      this.hidden = false;
      // Der Abgleich setzt auch die Ziffern auf "--" zurueck - nach dem Abgleich sofort neu schreiben
      requestAnimationFrame(() => this.#anzeigen());
    }
  }

  connectedCallback() {
    this.#ende = berlinZuEpoche(this.dataset.ende);
    if (this.#ende === null) return;
    if (document.documentElement.getAttribute('data-zw-angebot') === 'abgelaufen' || Date.now() >= this.#ende) {
      this.#verschwinden();
      return;
    }
    this.#anzeigen();
    this.hidden = false;
    this.#takt = setInterval(() => this.#anzeigen(), 1000);
    document.addEventListener('zw:angebot-abgelaufen', this.#verschwinden, { once: true });
  }

  disconnectedCallback() {
    if (this.#takt) clearInterval(this.#takt);
    this.#takt = null;
    document.removeEventListener('zw:angebot-abgelaufen', this.#verschwinden);
  }

  #anzeigen() {
    const rest = Math.max(0, (this.#ende ?? 0) - Date.now());
    if (rest === 0) {
      ablaufMelden();
      return;
    }
    const gesamt = Math.floor(rest / 1000);
    const tage = Math.floor(gesamt / 86400);
    const std = Math.floor((gesamt % 86400) / 3600);
    const min = Math.floor((gesamt % 3600) / 60);
    const sek = gesamt % 60;
    const setze = (/** @type {string} */ name, /** @type {number} */ wert) => {
      const ziel = this.querySelector(`[data-zw-${name}]`);
      const text = String(wert).padStart(2, '0');
      if (ziel && ziel.textContent !== text) ziel.textContent = text;
    };
    setze('tage', tage);
    setze('std', std);
    setze('min', min);
    setze('sek', sek);
  }

  #verschwinden = () => {
    if (this.#takt) clearInterval(this.#takt);
    this.#takt = null;
    this.hidden = true;
    this.closest('.zw-angebot__karte')?.setAttribute('hidden', '');
  };
}

class ZwAngebotSteuerung extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #warten = null;

  connectedCallback() {
    const ende = berlinZuEpoche(this.dataset.ende);
    if (ende === null) return;
    const rest = ende - Date.now();
    if (rest <= 0) {
      ablaufMelden();
      return;
    }
    // Ein Zeitgeber reicht bis knapp 25 Tage; danach gibt es nichts zu tun - die Seite wird längst neu geladen sein.
    if (rest + 250 < 2 ** 31) this.#warten = setTimeout(ablaufMelden, rest + 250);
  }

  disconnectedCallback() {
    if (this.#warten) clearTimeout(this.#warten);
    this.#warten = null;
  }
}

if (!customElements.get('zw-angebot-uhr')) customElements.define('zw-angebot-uhr', ZwAngebotUhr);
if (!customElements.get('zw-angebot-steuerung')) customElements.define('zw-angebot-steuerung', ZwAngebotSteuerung);
